import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { IDraggingContext, IProviderProps } from '../@types/ContextProviderRelated';

const draggingContextDefaultValues: IDraggingContext = {
    isDragging: false,
    setIsDragging: () => { },
}

const DraggingContext = createContext<IDraggingContext>(draggingContextDefaultValues);

export const useDraggingContext = () => useContext(DraggingContext);

export const DraggingProvider = ({ children }: IProviderProps): JSX.Element => {
    const [isDragging, setIsDragging] = useState<boolean>(draggingContextDefaultValues.isDragging);
    const value = useMemo(() => ({ isDragging, setIsDragging }), [isDragging]);

    useEffect(() => {
        if (!isDragging) return;

        const resetDragging = () => setIsDragging(false);
        const resetDraggingWhenHidden = () => {
            if (document.hidden) {
                resetDragging();
            }
        };
        const options: AddEventListenerOptions = { capture: true };

        window.addEventListener('pointerup', resetDragging, options);
        window.addEventListener('pointercancel', resetDragging, options);
        window.addEventListener('blur', resetDragging);
        document.addEventListener('visibilitychange', resetDraggingWhenHidden);

        return () => {
            window.removeEventListener('pointerup', resetDragging, options);
            window.removeEventListener('pointercancel', resetDragging, options);
            window.removeEventListener('blur', resetDragging);
            document.removeEventListener('visibilitychange', resetDraggingWhenHidden);
        };
    }, [isDragging]);

    return (
        <DraggingContext.Provider value={value}>
            {children}
        </DraggingContext.Provider>
    );
};
